import { defineStore } from "pinia";
import { useLocalConfigStore } from "./localConfig";
import { useNjordaApi } from "@/composables/njorda-api";
import type { CancelablePromise, AppSettings as TSettings  } from "@/interfaces/api";
import useUserSettings from "@/composables/user-settings";

export const useRemoteConfigStore = defineStore("remoteConfig", () => {
    const appName = ref("Njorda");
    const settings = ref<TSettings | null>(null);
    const organization = ref<string | undefined>();
    const modules = reactive({
        fundChecker: {
            enabled: false,
        },
        analytics: {
            enabled: true,
        },
        positions: {
            enabled: true,
        },
        overview: {
            enabled: true,
        },
        overviewAdvanced: {
            enabled: true,
        },
        overviewBasic: {
            enabled: true,
        },
    });
    const defaultLocale = ref("");
    const cancelPromise = ref<null | CancelablePromise<TSettings>>(null);
    const isSettingsInProgress = ref(false);

    const userSettingsData = useUserSettings();
    const { userSettings, pending: userSettingsPending } = userSettingsData;
    const localConfigStore = useLocalConfigStore();
    const { $i18n } = useNuxtApp();

    const pending = computed(
        () => userSettingsPending.value || isSettingsInProgress.value,
    );

    watch(
        userSettings,
        (newVal) => {
            if (newVal) {
                localConfigStore.$patch({
                    theme: newVal.dark_mode ? "njorda-dark" : "njorda",
                });
                localConfigStore.modules.overviewAdvanced.enabled =
                    newVal.overview_option === "advanced";
                localConfigStore.modules.overviewBasic.enabled =
                    newVal.overview_option === "basic";
                if (newVal.user_preferred_language) {
                    defaultLocale.value = newVal.user_preferred_language;
                    $i18n.setLocale(defaultLocale.value as "sv" | "en");
                }
            } else {
                if (settings.value?.app_language && !defaultLocale.value) {
                    defaultLocale.value =
                        userSettings.value?.user_preferred_language ??
                        settings.value?.app_language ??
                        "";
                    $i18n.setLocale(defaultLocale.value as "sv" | "en");
                }
            }
        },
        {
            immediate: true,
            deep: true,
        },
    );

    watch(
        organization,
        async (newVal) => {
            if (cancelPromise.value) {
                cancelPromise.value.cancel();
                return;
            }

            isSettingsInProgress.value = true;
            const { api } = useNjordaApi();
            cancelPromise.value = api.getAppV1Settings(newVal);
            const settingsRes = await cancelPromise.value.catch(() => null);

            cancelPromise.value = null;
            settings.value = settingsRes;

            if (settings.value?.app_language && !defaultLocale.value) {
                defaultLocale.value =
                    userSettings.value?.user_preferred_language ??
                    settings.value?.app_language ??
                    "";
                $i18n.setLocale(defaultLocale.value as "sv" | "en");
            }
            isSettingsInProgress.value = false;
        },
        {
            immediate: true,
        },
    );

    function setOrganization(organizationParam?: string) {
        organization.value = organizationParam;
    }

    return {
        appName,
        settings,
        organization,
        modules,
        defaultLocale,
        inProgress: pending,

        setOrganization,
    };
});
